import React from "react";
import {
	FieldValues,
	UseFormRegister
} from "react-hook-form";
import t, { changeLanguage } from "../../../components/I18N/TranslateHelpers";
import { GetCurrentLocale } from "../../../components/Utils";
import { useSelector } from "react-redux";
import { RootState } from "app/RootReducer";
import { store } from "app/store";
import { getLetter } from "features/Slice/LetterSlice";
import { LetterLanguageEnum, LetterTypeEnum } from "features/Entity/Letter";
import { SpinnerOnTop } from "components/SpinnerOnTop";
import RenderTextAreaWithLimitCharacters from "components/Forms/RenderTextAreaWithLimitCharacters";

interface ComponentProps {
	applicationFormId: string;
	register: UseFormRegister<FieldValues>;
	errors: { [x: string]: any };
	letterType: LetterTypeEnum;
	isReadOnly: boolean;
}

const LetterForm: React.FC<ComponentProps> = ({
	applicationFormId,
	register,
	errors,
	letterType,
	isReadOnly,
}) => {
	const isRequestingLetters = useSelector((state: RootState) => state.letterRdc.isRequesting);
	const letters = useSelector((state: RootState) => state.letterRdc.letters);
	const [expandedReviews, setExpandedReviews] = React.useState<{ [key: number]: boolean }>({});

	const userInfo = useSelector((state: RootState) => state.userRdc.userInfo);

	React.useEffect(() => {
		const locale = GetCurrentLocale();
		changeLanguage(locale);

		store.dispatch(getLetter(applicationFormId, letterType));
	}, []);

	const toggleReview = (index: number) => {
		setExpandedReviews((prev) => ({
			...prev,
			[index]: !prev[index],
		}));
	};

	return (
		<React.Fragment>
			{
				isRequestingLetters ?
					<SpinnerOnTop/>
				:
				letters !== null ?
					letters.map((letter, index) => (
						<React.Fragment key={index}>
							<input
								type="hidden"
								id={`letters[${index}].id`}
								value={letter.id}
								{...register(`letters[${index}].id`)}
							/>
							<input
								type="hidden"
								id={`letters[${index}].applicationFormId`}
								value={letter.applicationFormId}
								{...register(`letters[${index}].applicationFormId`)}
							/>
							<input
								type="hidden"
								id={`letters[${index}].letterTemplate.id`}
								value={letter.letterTemplate?.id}
								{...register(`letters[${index}].letterTemplate.id`)}
							/>
							<input
								type="hidden"
								id={`letters[${index}].letterTemplate.letterType`}
								value={letter.letterTemplate?.letterType}
								{...register(`letters[${index}].letterTemplate.letterType`)}
							/>
							<input
								type="hidden"
								id={`letters[${index}].letterTemplate.letterLanguage`}
								value={letter.letterTemplate?.letterLanguage}
								{...register(`letters[${index}].letterTemplate.letterLanguage`)}
							/>

							<RenderTextAreaWithLimitCharacters
								label={letter.letterTemplate.description ?? ""} 
								name={`letters[${index}].letterText`}
								defaultValue={letter.letterText ?? ""}
								errors={errors.letterText}
								register={register}
								required={letter.letterTemplate.letterLanguage !== LetterLanguageEnum.English ? true : false}
								classes={"col-md-12 col-sm-12 mb-3"}
								isDisabled={false}
								readOnly={isReadOnly}
								rows={25}
								isLabelShow={true}
								countCharacterMax={letter.maxLenght}
								countCharacterMin={letter.minLenght}
							/>							

							{/*  Display AI Student Letter Review  */}
							{userInfo?.isStaff &&
							<div className="ai-review mb-3">
								
								{letter.letterTextReview ? (
								<div className="card bg-light text-dark">
									<div className="d-flex card-header align-items-center bg-light text-dark">
										<span className="rounded-circle ai-review img-40 mt-0 d-flex justify-content-center mr-2"> <img src="../assets/images/icon-ai.svg" alt="Ai review" /></span>
										<label className="mb-0">{letter.letterTemplate.letterType === LetterTypeEnum.Applicant ? t('student_letter_review') : t('parent_letter_review')}</label>		
									</div>							
									<div className="card-body pt-0">
										<div
											style={{
												whiteSpace: "pre-wrap",
												maxHeight: expandedReviews[index] ? "none" : "150px",
												overflow: expandedReviews[index] ? "visible" : "hidden",
											}}
											dangerouslySetInnerHTML={{ __html: letter.letterTextReview }}
										></div>
									</div>
									<div className="card-footer d-flex justify-content-center py-0">
										<button
											type="button"
											onClick={() => toggleReview(index)}
											className="btn btn-link p-0 text-dark"
											aria-label={expandedReviews[index] ? "Réduire" : "Afficher tout"}
										>
											<i className={`icon-arrow-circle-${expandedReviews[index] ? "up" : "down"} text-dark`}></i>
										</button>
									</div>
								</div>
								) : null
								}									
							</div>
							}
						</React.Fragment>
					))
				: ""
			}
		</React.Fragment>
	);
};

export default LetterForm;
